<script setup lang="ts">
import { computed, ref, inject, onMounted, onBeforeUnmount } from 'vue';
import { subscribeKey, unsubscribeKey } from '@drapejs/core';
import { X, CircleHelp, Gift } from 'lucide-vue-next';

import useContext from '@/composables/useContext';
import useGiftCard from '@/composables/useGiftCard';
import useWebsiteTexts from '@/composables/useWebsiteTexts';
import useGiftProductListing, { generateHtmlElementId } from '@/composables/useGiftProductListing';

import Logout from '@/components/header/Logout.vue';
import LocaleSelector from '@/components/LocaleSelector.vue';

defineProps<{
  links: Array<any>;
  logoUrl?: string;
}>();

const emit = defineEmits<{ (e: 'close'): void }>();

const { channel, header, navigate } = useContext();
const { activeGiftCard } = useGiftCard();
const { websiteText } = useWebsiteTexts();
const { productSections } = useGiftProductListing();

const hasActiveGiftCard = computed(() => (activeGiftCard.value?.giftCards?.length ?? 0) > 0);

const customerServiceLink = computed<any>(() => header.value?.topNavigatonLink);
const customerServiceUrl = computed(() => customerServiceLink.value?.url);
const customerServiceLabel = computed(() => customerServiceLink.value?.label);

const redeemUrl = computed(() => channel.value?.rootPath || '/');

const cacheSubscribe = inject<(key: string, handler: (val: any) => void, options?: { immediate?: boolean }) => void>(subscribeKey, () => {});
const cacheUnsubscribe = inject<(key: string, handler: (val: any) => void) => void>(unsubscribeKey, () => {});

const categoryUrl = ref('');
function onCategoryUrl(val: any) {
  categoryUrl.value = val || '';
}
onMounted(() => cacheSubscribe('__categoryUrl', onCategoryUrl, { immediate: true }));
onBeforeUnmount(() => cacheUnsubscribe('__categoryUrl', onCategoryUrl));

const listingUrl = computed(() => categoryUrl.value || activeGiftCard.value?.firstCategory?.url || '');

const categories = computed<{ id: string; label: string; href: string }[]>(() => {
  const sections = (productSections.value || []) as any[];
  return sections
    .filter((s: any) => s.tags?.length > 0 && s.products?.length > 0)
    .map((s: any) => {
      const tag = s.tags[0];
      const id = generateHtmlElementId(tag.value);
      const base = listingUrl.value;
      const href = base
        ? `${base}${base.includes('?') ? '&' : '?'}scrollTo=${encodeURIComponent(tag.value || '')}`
        : id;
      return {
        id,
        label: tag.displayName || tag.value || '',
        href
      };
    });
});

function stickyHeight(selector: string) {
  const node = document.querySelector(selector) as HTMLElement | null;
  return node && node.offsetHeight > 0 ? node.getBoundingClientRect().height : 0;
}

function onCategoryClick(cat: { id: string; href: string }) {
  emit('close');

  if (document.getElementById(cat.id)) {
    setTimeout(() => {
      const el = document.getElementById(cat.id);
      if (!el) return;
      const headerH = stickyHeight('.site-header__mobile') || stickyHeight('.site-header__desktop');
      const navH = stickyHeight('.category-sticky-nav--mobile') || stickyHeight('.category-sticky-nav--desktop');
      const top = el.getBoundingClientRect().top + window.scrollY - headerH - navH;
      window.scrollTo({ top, behavior: 'smooth' });
    }, 100);
    return;
  }

  if (!cat.href || cat.href.startsWith('#')) return;

  const target = new URL(cat.href, window.location.origin);
  navigate(target.pathname + target.search);
}
</script>

<template>
  <aside
    class="mobile-header-menu fixed left-0 top-0 z-50 flex w-[calc(80vw+18px)] flex-col bg-background text-xs shadow-lg"
    role="dialog"
    aria-modal="true"
    aria-label="Navigation menu"
  >
    <div class="flex h-14 shrink-0 items-center justify-between border-b border-border bg-topbar px-4">
      <img v-if="logoUrl" :src="logoUrl" alt="logo" class="h-6" />
      <span v-else />
      <button type="button" class="btn-bare -mr-2 p-2 text-foreground" aria-label="Close menu" @click="emit('close')">
        <X class="h-5 w-5" />
      </button>
    </div>
    <div class="flex-1 overflow-y-auto pb-[calc(env(safe-area-inset-bottom)_+_75px)]">
      <template v-if="hasActiveGiftCard">
        <nav v-if="categories.length" class="flex flex-col py-2">
          <a
            v-for="cat in categories"
            :key="cat.id"
            :href="cat.href"
            class="border-b border-border px-4 py-3 text-sm uppercase font-normal text-foreground no-underline last:border-b-0 hover:underline"
            @click.prevent="onCategoryClick(cat)"
          >
            {{ cat.label }}
          </a>
        </nav>
      </template>
      <template v-else>
        <a
          :href="redeemUrl"
          class="animate-dark-gradient mx-3 my-4 flex items-center gap-3 rounded-xl visited:text-white px-5 py-4 text-sm font-medium text-white no-underline shadow-lg"
        >
          <Gift class="h-5 w-5 text-white/80" />
          <span>{{ websiteText('header__redeem_giftcard').value || 'Lös in gåvokort' }}</span>
        </a>
      </template>

      <div class="border-t border-border bg-topbar">
        <a
          v-if="customerServiceUrl"
          :href="customerServiceUrl"
          class="flex items-center gap-3 border-b border-border visited:text-foreground px-4 py-3 text-sm text-foreground no-underline hover:underline"
          @click="emit('close')"
        >
          <CircleHelp class="h-4 w-4 text-muted-foreground" />
          <span>{{ customerServiceLabel }}</span>
        </a>
        <Logout
          v-if="hasActiveGiftCard"
          :show-icon="true"
          class="flex cursor-pointer items-center gap-3 border-b border-border px-4 py-3 text-sm text-foreground hover:underline"
        />
        <div class="px-2 py-4">
          <LocaleSelector />
        </div>
      </div>
    </div>
  </aside>
</template>

<style scoped>
.mobile-header-menu {
  height: 100vh;
  height: 100dvh;
}
</style>
